import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { Sidebar } from '../../shared/components/sidebar/sidebar';
import { Navbar } from '../../shared/components/navbar/navbar';
import { PendingReviewsBanner } from '../../shared/components/pending-reviews-banner/pending-reviews-banner';
import { Toast } from '../../shared/services/toast';
import { Toast as ToastComponent } from '../../shared/components/toast/toast';
import { ClassifiedDocuments, ProcessedDocumentRow } from '../../core/services/classified-documents';
import { BrandIcon } from '../../shared/components/brand-icon/brand-icon';
import { GitHubConnector, GitHubRepository } from '../../core/services/github-connector';

const PAGE_SIZE = 10;

type ClassificationTab = 'documents' | 'repos';

type RepoWithConnection = GitHubRepository & { connectionId: string };

@Component({
  selector: 'app-classified-documents',
  standalone: true,
  imports: [CommonModule, FormsModule, Sidebar, Navbar, ToastComponent, PendingReviewsBanner, BrandIcon],
  templateUrl: './classified-documents.html',
  styleUrl: './classified-documents.css'
})
export class ClassifiedDocumentsPage implements OnInit {
  private router = inject(Router);
  private classifiedDocsService = inject(ClassifiedDocuments);
  private githubService = inject(GitHubConnector);
  private toast = inject(Toast);

  activeTab = signal<ClassificationTab>('documents');

  isLoading = signal(true);
  allDocuments = signal<ProcessedDocumentRow[]>([]);

  searchQuery = signal('');
  dataSourceFilter = signal('');
  classificationFilter = signal('');
  subClassificationFilter = signal('');

  // Repos tab state
  isLoadingRepos = signal(false);
  reposLoaded = signal(false);
  allRepos = signal<RepoWithConnection[]>([]);

  repoSearchQuery = signal('');
  repoLanguageFilter = signal('');
  repoVisibilityFilter = signal('');
  repoCurrentPage = signal(1);

  // Backed by the service so the selected page survives navigating away and back.
  currentPage = this.classifiedDocsService.currentPage;
  readonly pageSize = PAGE_SIZE;

  dataSourceOptions = computed(() => this.distinct(this.allDocuments().map(d => d.dataSource)));
  classificationOptions = computed(() => this.distinct(this.allDocuments().map(d => d.classification)));
  subClassificationOptions = computed(() => this.distinct(this.allDocuments().map(d => d.subClassification)));

  filteredDocuments = computed(() => {
    const search = this.searchQuery().trim().toLowerCase();
    const dataSource = this.dataSourceFilter();
    const classification = this.classificationFilter();
    const subClassification = this.subClassificationFilter();

    return this.allDocuments().filter(d => {
      if (search && !d.name.toLowerCase().includes(search)) return false;
      if (dataSource && d.dataSource !== dataSource) return false;
      if (classification && d.classification !== classification) return false;
      if (subClassification && d.subClassification !== subClassification) return false;
      return true;
    });
  });

  totalPages = computed(() => Math.max(1, Math.ceil(this.filteredDocuments().length / this.pageSize)));
  pagedDocuments = computed(() => {
    const start = (this.currentPage() - 1) * this.pageSize;
    return this.filteredDocuments().slice(start, start + this.pageSize);
  });

  languageOptions = computed(() => this.distinct(this.allRepos().map(r => r.language || '')));

  filteredRepos = computed(() => {
    const search = this.repoSearchQuery().trim().toLowerCase();
    const language = this.repoLanguageFilter();
    const visibility = this.repoVisibilityFilter();

    return this.allRepos().filter(r => {
      if (search && !r.name.toLowerCase().includes(search)) return false;
      if (language && r.language !== language) return false;
      if (visibility && (r.isPrivate ? 'Private' : 'Public') !== visibility) return false;
      return true;
    });
  });

  totalRepoPages = computed(() => Math.max(1, Math.ceil(this.filteredRepos().length / this.pageSize)));
  pagedRepos = computed(() => {
    const start = (this.repoCurrentPage() - 1) * this.pageSize;
    return this.filteredRepos().slice(start, start + this.pageSize);
  });

  ngOnInit() {
    this.loadDocuments();
    if (history.state?.activeTab === 'repos') {
      this.setActiveTab('repos');
    }
  }

  setActiveTab(tab: ClassificationTab) {
    this.activeTab.set(tab);
    if (tab === 'repos' && !this.reposLoaded()) {
      this.loadRepos();
    }
  }

  loadRepos() {
    this.isLoadingRepos.set(true);
    this.githubService.getConnections().subscribe({
      next: (connRes) => {
        const connections = connRes.data.connections || [];
        if (connections.length === 0) {
          this.isLoadingRepos.set(false);
          this.reposLoaded.set(true);
          this.allRepos.set([]);
          return;
        }
        forkJoin(connections.map(c =>
          this.githubService.getProcessedRepositories(c.id).pipe(
            map(res => (res.data.repositories || []).map(r => ({ ...r, connectionId: c.id })))
          )
        )).subscribe({
          next: (results) => {
            this.isLoadingRepos.set(false);
            this.reposLoaded.set(true);
            this.allRepos.set(results.flat());
          },
          error: (err) => {
            this.isLoadingRepos.set(false);
            const message = err?.error?.message || 'Failed to load processed repositories.';
            this.toast.show(message, 'error');
          }
        });
      },
      error: (err) => {
        this.isLoadingRepos.set(false);
        const message = err?.error?.message || 'Failed to load GitHub connections.';
        this.toast.show(message, 'error');
      }
    });
  }

  loadDocuments() {
    this.isLoading.set(true);
    this.classifiedDocsService.getAllProcessedDocuments().subscribe({
      next: (docs) => {
        this.isLoading.set(false);
        this.allDocuments.set(docs);
        if (this.currentPage() > this.totalPages()) {
          this.currentPage.set(this.totalPages());
        }
      },
      error: (err) => {
        this.isLoading.set(false);
        const message = err?.error?.message || 'Failed to load classified documents.';
        this.toast.show(message, 'error');
      }
    });
  }

  private distinct(values: string[]): string[] {
    return Array.from(new Set(values.filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }

  onSearch(event: Event) {
    this.searchQuery.set((event.target as HTMLInputElement).value);
    this.currentPage.set(1);
  }

  onDataSourceFilterChange(event: Event) {
    this.dataSourceFilter.set((event.target as HTMLSelectElement).value);
    this.currentPage.set(1);
  }

  onClassificationFilterChange(event: Event) {
    this.classificationFilter.set((event.target as HTMLSelectElement).value);
    this.currentPage.set(1);
  }

  onSubClassificationFilterChange(event: Event) {
    this.subClassificationFilter.set((event.target as HTMLSelectElement).value);
    this.currentPage.set(1);
  }

  goToPage(page: number) {
    if (page < 1 || page > this.totalPages()) return;
    this.currentPage.set(page);
  }

  paginationItems(): (number | '…')[] {
    const total = this.totalPages();
    const current = this.currentPage();
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const items: (number | '…')[] = [1];
    if (current > 3) items.push('…');

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let p = start; p <= end; p++) items.push(p);

    if (current < total - 2) items.push('…');
    items.push(total);
    return items;
  }

  fullPageNumbers(): number[] {
    return Array.from({ length: this.totalPages() }, (_, i) => i + 1);
  }

  onPageSelect(event: Event) {
    this.goToPage(Number((event.target as HTMLSelectElement).value));
  }

  iconClass(fileExtension: string): string {
    const ext = (fileExtension || '').toLowerCase().replace('.', '');
    if (ext === 'doc' || ext === 'docx') return 'doc-word';
    if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return 'doc-excel';
    if (ext === 'ppt' || ext === 'pptx') return 'doc-ppt';
    return 'doc-generic';
  }

  viewDetails(doc: ProcessedDocumentRow) {
    this.router.navigate(['/document-access', doc.id], {
      state: { docName: doc.name, batchId: doc.batchId, from: 'classification' }
    });
  }

  onRepoSearch(event: Event) {
    this.repoSearchQuery.set((event.target as HTMLInputElement).value);
    this.repoCurrentPage.set(1);
  }

  onRepoLanguageFilterChange(event: Event) {
    this.repoLanguageFilter.set((event.target as HTMLSelectElement).value);
    this.repoCurrentPage.set(1);
  }

  onRepoVisibilityFilterChange(event: Event) {
    this.repoVisibilityFilter.set((event.target as HTMLSelectElement).value);
    this.repoCurrentPage.set(1);
  }

  goToRepoPage(page: number) {
    if (page < 1 || page > this.totalRepoPages()) return;
    this.repoCurrentPage.set(page);
  }

  repoPaginationItems(): (number | '…')[] {
    const total = this.totalRepoPages();
    const current = this.repoCurrentPage();
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

    const items: (number | '…')[] = [1];
    if (current > 3) items.push('…');

    const start = Math.max(2, current - 1);
    const end = Math.min(total - 1, current + 1);
    for (let p = start; p <= end; p++) items.push(p);

    if (current < total - 2) items.push('…');
    items.push(total);
    return items;
  }

  repoFullPageNumbers(): number[] {
    return Array.from({ length: this.totalRepoPages() }, (_, i) => i + 1);
  }

  onRepoPageSelect(event: Event) {
    this.goToRepoPage(Number((event.target as HTMLSelectElement).value));
  }

  formatRepoSize(sizeKb: number): string {
    if (!sizeKb) return '-';
    if (sizeKb >= 1024) return (sizeKb / 1024).toFixed(1) + ' MB';
    return sizeKb + ' KB';
  }

  formatRepoDate(repo: RepoWithConnection): string {
    const raw = repo.pushedAt || repo.updatedAt;
    if (!raw) return '-';
    const date = new Date(raw);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  viewRepoGraph(repo: RepoWithConnection) {
    this.router.navigate(['/data-sources/repositories/graph'], {
      queryParams: { repo: repo.name, connectionId: repo.connectionId },
      state: { from: 'classification' }
    });
  }
}
